import {
  Component,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  ResolveEnd,
  Router,
  RoutesRecognized,
} from '@angular/router';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import type { Message } from '../../../server/types/message-types';
import type { OrgUser } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService, type UserOrgObj } from '../auth-user.service';
import {
  generateInboxViewPath,
  getInboxRefId,
  type InboxItemType,
  InboxService,
} from '../inbox.service';
import { MessageService, ThreadItem } from '../message.service';
import { SettingsService, type ThemeString } from '../settings.service';

dayjs.extend(calendar);
@Component({
  selector: 'app-inbox-list-item',
  templateUrl: './inbox-list-item.component.html',
  styleUrls: ['./inbox-list-item.component.scss'],
  standalone: false,
})
export class InboxListItemComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private messageService = inject(MessageService);
  private inboxService = inject(InboxService);
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @Input() inboxItem: InboxItemType;
  @Input() inArchive = false;

  threadItem: ThreadItem;

  isSelected = false;
  orgId: string;
  refId: string;
  userOrgObj: DeepReadonly<UserOrgObj>;
  authUser: OrgUser;
  inboxPath: string | undefined;
  title?: string;
  displayTime = '';
  unreadMessageCount = 0;
  latestMessage?: Message;
  latestMessageSender?: OrgUser;

  themeClass: ThemeString = 'dark-theme';

  // Menu variables
  enableLeave = true;

  error?: string;
  subscriptions = new Subscription();

  ngOnInit(): void {
    const { orgId, refId } = this.inboxItem;

    const mode = this.inboxItem.inboxMode;

    if (
      mode !== 'task' &&
      mode !== 'thread' &&
      mode !== 'direct' &&
      mode !== 'channel' &&
      mode !== 'space' &&
      mode !== 'announcement'
    ) {
      const errMessage = 'Mode not implemented yet: ' + mode;
      this.error = 'Error: ' + errMessage;
      throw new Error(errMessage);
    }
    const threadItem = this.messageService.getThreadItem({
      orgId,
      refId,
      mode,
    });

    this.threadItem = threadItem;

    const routerSub = this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationEnd ||
        event instanceof ResolveEnd ||
        event instanceof RoutesRecognized
      ) {
        const url = event.urlAfterRedirects;
        this.determineIfSelected(url);
      }
    });
    this.subscriptions.add(routerSub);
    const routeSub = this.route.url.subscribe(() => {
      this.determineIfSelected(this.router.url);
    });
    this.subscriptions.add(routeSub);

    this.setDisplayTime({ time: this.inboxItem.time });

    this.orgId = orgId;
    this.refId = refId;
    const userOrgObjSubscription = this.authUserService.userOrgObj
      .pipe(take(1))
      .subscribe((userOrgObj) => {
        if (!userOrgObj) return;

        this.userOrgObj = userOrgObj;
        this.authUser = userOrgObj.user;

        this.setupSubscriptions(threadItem);
      });
    this.subscriptions.add(userOrgObjSubscription);

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);
  }

  setupSubscriptions(threadItem: ThreadItem): void {
    // title
    const titleSubscribe = threadItem.titleReplayObservable.subscribe(
      (title) => (this.title = title)
    );
    this.subscriptions.add(titleSubscribe);

    // Push new messages in
    const liveMessagesSubscription =
      threadItem.latestMessageObservable.subscribe((message) => {
        this.latestMessage = message;
        const { orgId, userId } = message;
        this.latestMessageSender = this.authUserService.getUser({
          orgId,
          userId,
        });
      });
    this.subscriptions.add(liveMessagesSubscription);

    // Update time
    const { orgId, refId, authUserId } = threadItem;
    const receivedAtSubscription = this.inboxService
      .getInboxObj({ orgId, refId, authUserId })
      .subscribe(({ time }) => this.setDisplayTime({ time }));
    this.subscriptions.add(receivedAtSubscription);

    // Update unread count
    const unreadMessageCountSubscription =
      threadItem.unreadMessageCountReplayObservable.subscribe((count) => {
        this.unreadMessageCount = count;
      });
    this.subscriptions.add(unreadMessageCountSubscription);

    // Determine if the auth user can leave
    const initialUserIds = (threadItem.thread ?? threadItem.task)?.userIds;
    if (initialUserIds) this.determineEnableLeave({ userIds: initialUserIds });
    const userSub = threadItem.userIdsReplayObservable.subscribe((userIds) => {
      this.determineEnableLeave({ userIds });
    });
    this.subscriptions.add(userSub);
  }

  determineIfSelected(windowPath: string) {
    const { orgId, refId } = this.inboxItem;

    this.isSelected = getInboxRefId({ path: windowPath }) === refId;

    if (this.isSelected) {
      this.inboxPath = undefined;
    } else if (!this.inboxPath) {
      const mode = this.inboxItem.inboxMode;

      this.inboxPath = generateInboxViewPath({
        orgId,
        refId,
        mode,
        archive: this.inArchive,
      });
    }
  }

  determineEnableLeave({ userIds }: { userIds: string[] }) {
    this.enableLeave =
      userIds.includes(this.authUser._id) &&
      (this.threadItem.mode === 'thread' ||
        this.threadItem.task?.assignedTo !== this.authUser._id);
  }

  setDisplayTime({ time }: { time: number }) {
    const currentDate = new Date();
    if (currentDate.toDateString() === new Date(time).toDateString()) {
      this.displayTime = dayjs(time).format('LT');
    } else if (
      currentDate.getUTCFullYear() === new Date(time).getUTCFullYear()
    ) {
      this.displayTime = dayjs(time).format('MMM D');
    } else {
      this.displayTime = dayjs(time).format('MMM D YYYY');
    }
  }

  archive() {
    const { orgId, refId } = this;
    const authUserId = this.authUser._id;
    this.inboxService.archive({ orgId, refId, authUserId });
  }

  unarchive() {
    const { orgId, refId } = this;
    const authUserId = this.authUser._id;
    this.inboxService.unarchive({ orgId, refId, authUserId });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
